import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Skeleton,
  StatTile,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  type BadgeTone,
} from '@provenance/design';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Inbox, Send } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';

type Status = 'SENT' | 'FAILED' | 'NOT_SENT' | 'PENDING';

interface Overview {
  transport: string;
  delivering: boolean;
  fromAddress: string;
  fromName: string;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUserSet: boolean;
  last24h: Record<Status, number>;
  last7d: Record<Status, number>;
  queues: Array<{ name: string; waiting: number; active: number; failed: number; delayed: number; reachable: boolean }>;
}

interface Row {
  id: string;
  recipient: string;
  subject: string;
  templateKey: string;
  status: string;
  errorMessage: string | null;
  chamber: string | null;
  createdAt: string;
  sentAt: string | null;
}

interface Detail extends Row {
  cc: string[];
  providerId: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
}

const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  SENT: { label: 'Sent', tone: 'ok' },
  FAILED: { label: 'Failed', tone: 'stop' },
  NOT_SENT: { label: 'Not sent', tone: 'warn' },
  PENDING: { label: 'Sending', tone: 'info' },
};

const FILTERS: Array<{ value: Status | undefined; label: string }> = [
  { value: undefined, label: 'All' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'NOT_SENT', label: 'Not sent' },
  { value: 'PENDING', label: 'Sending' },
];

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Email diagnostics — Mailpit for the platform team.
 *
 * Answers "why didn't they get the email?" without a shell: is email switched on at all,
 * did the provider refuse it (and with what error), and what exactly did it say.
 */
export function Email() {
  const [status, setStatus] = useState<Status | undefined>();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['email-overview'],
    queryFn: () => api.get<Overview>('/api/console/platform/email/overview'),
    refetchInterval: 15_000,
  });

  const params = new URLSearchParams({ page: String(page) });
  if (status) params.set('status', status);
  if (search.trim()) params.set('q', search.trim());
  const messages = useQuery({
    queryKey: ['email-messages', status, search.trim(), page],
    queryFn: () =>
      api.get<{ total: number; page: number; pageSize: number; items: Row[] }>(
        `/api/console/platform/email/messages?${params.toString()}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });

  const o = overview.data;
  const totalPages = messages.data ? Math.max(1, Math.ceil(messages.data.total / messages.data.pageSize)) : 1;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Email"
        lede="Every email the platform has tried to send — invitations, password resets, certificate notices — with what happened to each. Refreshes every 15 seconds."
      />

      {overview.isLoading ? (
        <Skeleton className="h-24" />
      ) : o && !o.delivering ? (
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Email is switched off on this server">
          <code>MAIL_TRANSPORT</code> is <code>log</code>: every email is written to the server log and
          marked “Not sent” below, and nobody receives anything. Set up Amazon SES and switch it to{' '}
          <code>smtp</code> — step 11 of the deployment guide. Until then, give people their
          activation link with <code>node dist/cli/create-chamber.js --resend-invite</code>.
        </Callout>
      ) : o ? (
        <Callout tone="ok" icon={<CheckCircle2 className="size-4" />} title={`Sending through ${o.transport.toUpperCase()}`}>
          From “{o.fromName}” &lt;{o.fromAddress}&gt;
          {o.smtpHost && (
            <>
              {' '}via <code>{o.smtpHost}:{o.smtpPort}</code>
              {!o.smtpUserSet && ' — with no SMTP user set, which most providers will refuse'}
            </>
          )}
          . Use “Send a test email” to prove it reaches a real inbox.
        </Callout>
      ) : null}

      {o && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="Sent · 24h" value={o.last24h.SENT} sublabel={`${o.last7d.SENT} in 7 days`} tone="good" onClick={() => { setStatus('SENT'); setPage(1); }} />
          <StatTile label="Failed · 24h" value={o.last24h.FAILED} sublabel={`${o.last7d.FAILED} in 7 days`} tone={o.last24h.FAILED ? 'stop' : 'neutral'} onClick={() => { setStatus('FAILED'); setPage(1); }} />
          <StatTile label="Not sent · 24h" value={o.last24h.NOT_SENT} sublabel={`${o.last7d.NOT_SENT} in 7 days`} tone={o.last24h.NOT_SENT ? 'warn' : 'neutral'} onClick={() => { setStatus('NOT_SENT'); setPage(1); }} />
          <StatTile label="Sending now" value={o.last24h.PENDING} sublabel="Stuck here for minutes means the provider is not answering" onClick={() => { setStatus('PENDING'); setPage(1); }} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card padded={false}>
          <div className="flex flex-wrap items-center gap-2 border-b border-[var(--pv-line)] p-3">
            <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <Button
                  key={f.label}
                  size="sm"
                  variant={status === f.value ? 'primary' : 'ghost'}
                  aria-pressed={status === f.value}
                  onClick={() => {
                    setStatus(f.value);
                    setPage(1);
                  }}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <Input
              className="ml-auto w-full sm:w-64"
              placeholder="Search recipient or subject"
              aria-label="Search recipient or subject"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {messages.isLoading ? (
            <Skeleton className="m-3 h-64" />
          ) : !messages.data?.items.length ? (
            <EmptyState
              icon={<Inbox className="size-5" />}
              title={status || search ? 'Nothing matches' : 'No emails yet'}
              description="Invitations, password resets and certificate notices appear here the moment they are sent."
            />
          ) : (
            <>
              <TableShell>
                <Table className="table-fixed">
                  <Thead>
                    <Tr>
                      <Th className="w-[9.5rem]">When</Th>
                      <Th>Email</Th>
                      <Th className="w-[7rem]">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {messages.data.items.map((m) => (
                      <Tr key={m.id} onClick={() => setOpenId(m.id)}>
                        <Td><span className="pv-num whitespace-nowrap [font-size:12.5px] text-[var(--pv-muted)]">{when(m.createdAt)}</span></Td>
                        <Td>
                          <span className="block min-w-0">
                            <button
                              type="button"
                              className="block max-w-full truncate text-left font-medium hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenId(m.id);
                              }}
                            >
                              {m.subject}
                            </button>
                            <span className="block truncate [font-size:12px] text-[var(--pv-faint)]">
                              To {m.recipient} · {m.chamber ?? 'Platform'}
                            </span>
                            {m.errorMessage && m.status === 'FAILED' && (
                              <span className="block truncate [font-size:12px] text-[var(--pv-stop)]">{m.errorMessage}</span>
                            )}
                          </span>
                        </Td>
                        <Td>
                          <Badge tone={STATUS[m.status]?.tone ?? 'neutral'} size="sm" dot>
                            {STATUS[m.status]?.label ?? m.status}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableShell>
              <div className="flex items-center gap-2 border-t border-[var(--pv-line)] p-3 [font-size:12.5px] text-[var(--pv-muted)]">
                <span>
                  {messages.data.total.toLocaleString('en-AU')} email{messages.data.total === 1 ? '' : 's'} · page {page} of {totalPages}
                </span>
                <Button size="sm" variant="ghost" className="ml-auto" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </>
          )}
        </Card>

        <div className="flex flex-col gap-4">
          <InboundCard />
          <TestEmailCard />
          {o && (
            <Card>
              <CardHeader
                title="Background job queues"
                description="Stamping, AI checks, webhooks and email jobs run in the worker. Waiting that keeps growing means the worker is down or stuck."
              />
              <dl className="grid gap-2 [font-size:13px]">
                {o.queues.map((q) => (
                  <KeyValue key={q.name} label={q.name}>
                    {!q.reachable ? (
                      <Badge tone="stop" size="sm">Unreachable</Badge>
                    ) : (
                      <span className="pv-num">
                        {q.waiting} waiting · {q.active} active
                        {q.failed > 0 && <span className="text-[var(--pv-stop)]"> · {q.failed} failed</span>}
                      </span>
                    )}
                  </KeyValue>
                ))}
              </dl>
            </Card>
          )}
        </div>
      </div>

      <MessageDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function TestEmailCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [to, setTo] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api.post<{ id: string; status: string; errorMessage: string | null }>('/api/console/platform/email/test', { to }),
    onSuccess: (r) => {
      if (r.status === 'SENT') toast.success('Test email sent', `Check ${to} — including the spam folder.`);
      else if (r.status === 'NOT_SENT') toast.error('Not sent', 'Email is switched off on this server (MAIL_TRANSPORT=log).');
      else toast.error('The provider refused it', r.errorMessage ?? 'See the email in the list for details.');
      void queryClient.invalidateQueries({ queryKey: ['email-messages'] });
      void queryClient.invalidateQueries({ queryKey: ['email-overview'] });
    },
    onError: (error) =>
      toast.error('Could not send', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <Card>
      <CardHeader title="Send a test email" description="The quickest way to know whether email really works end to end." />
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (to.trim()) send.mutate();
        }}
      >
        <Field label="Send to" required>
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" />
        </Field>
        <Button type="submit" variant="secondary" leadingIcon={<Send className="size-3.5" />} loading={send.isPending} disabled={!to.trim()}>
          Send test email
        </Button>
      </form>
    </Card>
  );
}

function MessageDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const [view, setView] = useState<'html' | 'text'>('html');
  const detail = useQuery({
    queryKey: ['email-message', id],
    queryFn: () => api.get<Detail>(`/api/console/platform/email/messages/${id}`),
    enabled: Boolean(id),
  });
  const m = detail.data;

  return (
    <Dialog open={Boolean(id)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[52rem]">
        <DialogHeader title={m?.subject ?? 'Email'} description={m ? `To ${m.recipient}` : undefined} />
        <DialogBody>
          {!m ? (
            <Skeleton className="h-80" />
          ) : (
            <div className="flex flex-col gap-4">
              <dl className="grid gap-1.5 [font-size:13px] sm:grid-cols-2">
                <KeyValue label="Status">
                  <Badge tone={STATUS[m.status]?.tone ?? 'neutral'} size="sm" dot>{STATUS[m.status]?.label ?? m.status}</Badge>
                </KeyValue>
                <KeyValue label="Chamber">{m.chamber ?? 'Platform'}</KeyValue>
                <KeyValue label="Created">{when(m.createdAt)}</KeyValue>
                <KeyValue label="Sent">{m.sentAt ? when(m.sentAt) : '—'}</KeyValue>
                <KeyValue label="Template" mono>{m.templateKey}</KeyValue>
                {m.cc.length > 0 && <KeyValue label="Cc">{m.cc.join(', ')}</KeyValue>}
                {m.providerId && <KeyValue label="Provider id" mono>{m.providerId}</KeyValue>}
              </dl>
              {m.errorMessage && (
                <Callout tone={m.status === 'FAILED' ? 'stop' : 'warn'} icon={<AlertTriangle className="size-4" />} title={m.status === 'FAILED' ? 'Error from the mail provider' : 'Why it was not sent'}>
                  <code className="break-all">{m.errorMessage}</code>
                </Callout>
              )}
              <div role="group" aria-label="View" className="flex gap-1">
                <Button size="sm" variant={view === 'html' ? 'primary' : 'ghost'} aria-pressed={view === 'html'} onClick={() => setView('html')}>As received</Button>
                <Button size="sm" variant={view === 'text' ? 'primary' : 'ghost'} aria-pressed={view === 'text'} onClick={() => setView('text')}>Plain text</Button>
              </div>
              {!m.bodyHtml && !m.bodyText ? (
                <p className="[font-size:13px] text-[var(--pv-muted)]">
                  This email was sent before copies were kept, so there is no body to show.
                </p>
              ) : view === 'html' && m.bodyHtml ? (
                // sandbox with no permissions: no scripts, no forms, no navigation of this page.
                <iframe
                  title={`Body of “${m.subject}”`}
                  sandbox=""
                  srcDoc={m.bodyHtml}
                  className="h-[28rem] w-full rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-white"
                />
              ) : (
                <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-3 [font-size:12.5px]">
                  {m.bodyText}
                </pre>
              )}
              <p className="[font-size:12px] text-[var(--pv-faint)]">
                Links that act as a password — activation, reset and upload links — are stored as
                [redacted]. To give someone a working link, use the command-line tool.
              </p>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

interface Inbound {
  last24h: Record<string, number>;
  lastReceivedAt: string | null;
  lastSnsDeliveryAt: string | null;
  lastError: { at: string; message: string } | null;
  mx: { domain: string | null; records: string[]; pointsAtSes: boolean; error?: string };
  serverReady: { domain: string | null; sesConfigured: boolean; outboundEmailOn: boolean };
}

/** The receiving half: is the path from SES configured, and is anything arriving? */
function InboundCard() {
  const inbound = useQuery({
    queryKey: ['email-inbound'],
    queryFn: () => api.get<Inbound>('/api/console/platform/email/inbound'),
    refetchInterval: 30_000,
  });
  const i = inbound.data;
  if (!i) return <Skeleton className="h-40" />;
  const received = Object.values(i.last24h).reduce((a, b) => a + b, 0);
  const rows: Array<[string, React.ReactNode]> = [
    ['Inbound domain', i.serverReady.domain ?? <Badge tone="warn" size="sm">Not set</Badge>],
    [
      'MX record',
      i.mx.pointsAtSes ? (
        <Badge tone="ok" size="sm" dot>Points at SES</Badge>
      ) : (
        <Badge tone="stop" size="sm" dot>{i.mx.error ? 'Missing' : 'Not SES'}</Badge>
      ),
    ],
    ['SES topic and bucket', i.serverReady.sesConfigured ? <Badge tone="ok" size="sm" dot>Configured</Badge> : <Badge tone="warn" size="sm">Not set</Badge>],
    ['Last from SNS', i.lastSnsDeliveryAt ? when(i.lastSnsDeliveryAt) : 'Never'],
    ['Last email received', i.lastReceivedAt ? when(i.lastReceivedAt) : 'Never'],
    ['Received · 24h', received],
  ];
  return (
    <Card>
      <CardHeader title="Inbound mail" description="Chamber mailboxes: Amazon SES receives, SNS tells Provenir. Each chamber sees its own mail in its Mailbox." />
      <dl className="grid gap-2 [font-size:13px]">
        {rows.map(([label, value]) => (
          <KeyValue key={label} label={label}>
            {value}
          </KeyValue>
        ))}
      </dl>
      {i.lastError && (
        <p className="mt-3 [font-size:12px] text-[var(--pv-stop)]">
          Last error {when(i.lastError.at)}: {i.lastError.message}
        </p>
      )}
    </Card>
  );
}
